import React, { ReactNode } from 'react';

import { isEqual, matchesProperty, pick } from 'lodash/fp';
import { OrderDirectionKey, PaginatedRequest, PaginatedResponse } from 'types';

import { parseApiError } from '../../utility/api-helpers';
import DataTableView from './DataTableView';

export type RenderProps = ReturnType<DataTable['getRenderProps']>;

type Items = Array<object> | null;

export interface Props {
    page?: number;
    perPage?: number;
    search?: string;
    orderBy?: string;
    orderDirection?: OrderDirectionKey;
    searchDebounce?: number;
    onPageChange: (value: number) => void;
    onSearchChange: (value: string) => void;
    onItemsLoaded: (state: State) => void;
    loadItems: <T = object>(queryParams: PaginatedRequest<T>) => Promise<PaginatedResponse<T>>;
    removeItem: <T = object>(id: number) => Promise<T>;
    renderActionButton?: (renderProps: RenderProps) => {};
    renderHeaders: ReactNode | ((renderProps: RenderProps) => ReactNode);
    renderItem: (item: any, renderProps: RenderProps) => {};
    renderFooter?: ReactNode | ((renderProps: RenderProps) => ReactNode | null);
    renderEmpty?: (renderProps: RenderProps) => {};
    showSearchField?: boolean;
    hidePagination?: boolean;
}

type State = {
    totalItems: number;
    page: number;
    itemsPerPage: number;
    items: Items | null;
    errors: {} | null;
    isLoading: boolean;
};

export const PARAM_KEYS = ['page', 'perPage', 'search', 'orderBy', 'orderDirection'];
const pickParams = pick(PARAM_KEYS);

class DataTable extends React.PureComponent<Props, State> {
    static defaultProps = {
        page: 1,
        perPage: 10,
        search: '',
        orderBy: 'name',
        searchDebounce: 250,
    };

    state = {
        items: null,
        totalItems: 0,
        itemsPerPage: 0,
        page: 0,
        errors: null,
        isLoading: false,
    };

    private justMountedRef = { current: true };

    componentDidMount() {
        this.loadItems();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.justMountedRef.current) {
            this.justMountedRef.current = false;
            return;
        }

        if (!isEqual(pickParams(this.props), pickParams(prevProps))) {
            this.loadItems();
        }
    }

    getRenderProps = () => ({
        params: this.getParams(),
        items: this.state.items,
        loadItems: this.loadItems,
        totalItemsAmount: this.state.totalItems,
        isLoading: this.state.isLoading,
        page: this.state.page,
        itemsPerPage: this.state.itemsPerPage,
        removeItem: this.removeItem,
        updateVisibleItem: this.updateVisibleItem,
        updateVisibleItems: this.updateVisibleItems,
    });

    getParams = () => {
        const { search, page, perPage, orderBy, orderDirection } = this.props;

        return {
            search,
            page,
            per_page: perPage,
            order_by: orderBy,
            order_direction: orderDirection,
        };
    };

    // External API to be used via refs
    getTotalItemsCount = () => this.state.totalItems;

    loadItems = () => {
        const callParams = this.getParams();
        this.setState({ isLoading: true });

        this.props
            .loadItems(callParams)
            .then((response) => {
                if (!isEqual(callParams, this.getParams())) return;

                const {
                    results: items,
                    paging: { total_count: totalItems, page, per_page: itemsPerPage },
                } = response;

                const maxPossiblePage = Math.max(1, Math.ceil(totalItems / itemsPerPage));

                if (page > maxPossiblePage) {
                    this.handlePageChange(maxPossiblePage);
                } else {
                    this.setState(
                        { items, totalItems, page, itemsPerPage, isLoading: false, errors: null },
                        () => {
                            this.props.onItemsLoaded?.(this.state);
                        }
                    );
                }
            })
            .catch((err) => {
                if (!isEqual(callParams, this.getParams())) return;
                this.setState({ isLoading: false }, () => this.handleError(err));
            });
    };

    removeItem = (id) => {
        return this.props
            .removeItem(id)
            .then((response) => {
                this.setState((state) => {
                    if (state.items) {
                        const items = state.items.filter((i) => i.id !== id);

                        return {
                            items,
                            totalItems: items.length,
                        };
                    }

                    return null;
                });
            })
            .catch(this.handleError);
    };

    updateVisibleItem = (
        updatedItem: object,
        isUpdatedItem?: (item: object) => boolean,
        onUpdate?: () => void
    ) => {
        const searchFunction = isUpdatedItem
            ? isUpdatedItem
            : matchesProperty('id', updatedItem.id);

        this.setState(
            ({ items }) => ({
                items: items.map((item) =>
                    searchFunction(item)
                        ? {
                              ...item,
                              ...updatedItem,
                          }
                        : item
                ),
            }),
            () => onUpdate && onUpdate()
        );
    };

    updateVisibleItems = (
        updater: (items: Items) => Items,
        onUpdate: (updatedItems: Items) => void
    ) =>
        this.setState(
            ({ items }) => ({
                items: updater(items),
            }),
            () => onUpdate && onUpdate(this.state.items)
        );

    handleError = (error: {}) => this.setState({ errors: parseApiError(error) });

    handlePageChange = (page: number) => this.props.onPageChange(page);

    handleSearchChange = (search: string) => this.props.onSearchChange(search);

    renderActionButton = () => {
        const { renderActionButton } = this.props;
        return renderActionButton && renderActionButton(this.getRenderProps());
    };

    renderHeaders = () => {
        const { renderHeaders } = this.props;
        return renderHeaders(this.getRenderProps());
    };

    renderItem = (item) => {
        const { renderItem } = this.props;
        return renderItem(item, this.getRenderProps());
    };

    renderFooter = () => {
        const { renderFooter } = this.props;
        return renderFooter && renderFooter(this.getRenderProps());
    };

    renderEmpty = () => {
        const { renderEmpty } = this.props;
        return renderEmpty && renderEmpty(this.getRenderProps());
    };

    render() {
        const { showSearchField, hidePagination, dataTableStyle, searchDebounce } = this.props;
        const { search, page, per_page } = this.getParams();

        const { items, totalItems, errors } = this.state;

        return (
            <>
                <DataTableView
                    dataTableStyle={{ ...dataTableStyle }}
                    renderActionButton={this.renderActionButton}
                    renderHeaders={this.renderHeaders}
                    renderItem={this.renderItem}
                    renderEmpty={this.renderEmpty}
                    areItemsLoaded={items != null}
                    items={items}
                    totalItems={totalItems}
                    errors={errors}
                    page={page}
                    perPage={per_page}
                    onPageChange={this.handlePageChange}
                    search={search}
                    searchDebounce={searchDebounce}
                    onDebouncedSearch={this.handleSearchChange}
                    showSearchField={showSearchField}
                    hidePagination={hidePagination}
                />

                {this.renderFooter()}
            </>
        );
    }
}

export default DataTable;
